import { useEffect, useRef } from "react";
import cx from "classnames";
import { AnimatePresence, motion, Variants } from "framer-motion";
import ScrollLock from "react-scrolllock";
import { ModalContent } from "./ModalContent";
import { ModalContext } from "./ModalContext";
import styles from "./Modal.module.scss";
import { useHotkeys } from "react-hotkeys-hook";
import { KeyboardKeys } from "enum/keyboardkeys.enum";
import { tabbable } from "tabbable";
import { FocusLock } from "../FocusLock";
import { useTouchScrollable } from "./useTouchScrollable";

export const Sizes = {
  xs: 300,
  sm: 500,
  // sm plus 15% (the Services page request form).
  "sm-wide": 575,
  md: 700,
  "md-wide": 720,
  lg: 800,
  xl: 900,
  "2xl": 1000,
  "3xl": 1100,
};

export type ModalSizes =
  | "xs"
  | "sm"
  | "sm-wide"
  | "md"
  | "md-wide"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "full";

export interface IModalProps {
  children: React.ReactNode;
  isOpen: boolean;
  isCentered?: boolean;
  isOnTop?: boolean;
  closeOnOverlayClick?: boolean;
  size?: ModalSizes;
  trapFocus?: boolean;
  isFullWidth?: boolean;
  scrollMode?: "inner" | "outside";
  testId?: string;

  handleClose?(): void;
}

const animation: Variants = {
  visible: { opacity: 1, scale: 1, y: -30 },
  hidden: { opacity: 0, scale: 0.99, y: 0 },
};

export const Modal = ({
  children,
  isOpen = false,
  handleClose = () => undefined,
  size = "sm",
  closeOnOverlayClick = true,
  trapFocus = true,
  isFullWidth,
  isCentered,
  testId,
  isOnTop = true,
  scrollMode = "outside",
}: IModalProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Wide modals (forms such as Create Shipment) become an edge-to-edge sheet
  // on phones; small dialogs keep their centered card.
  const isWide = size === "full" || Sizes[size] >= Sizes.lg;

  useTouchScrollable(containerRef, isOpen);

  useHotkeys(
    KeyboardKeys.ESCAPE,
    () => {
      handleClose();
    },
    {
      enableOnTags: ["INPUT"],
    }
  );

  useEffect(() => {
    if (contentRef && contentRef.current) {
      const elements = tabbable(contentRef.current);
      elements.length > 0 && elements[0].focus();
    }
  }, [isOpen, contentRef]);

  return (
    <FocusLock disabled={!isOpen} autoFocus={false} returnFocus={true}>
      <ScrollLock isActive={isOpen} />
      <ModalContext
        value={{
          handleClose,
          trapFocus,
          size,
          isCentered,
          isFullWidth,
          scrollMode,
          isOpen,
          isOnTop,
        }}
      >
        <AnimatePresence>
          {isOpen && (
            <motion.div
              key={1}
              ref={containerRef}
              variants={animation}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.2, stiffness: 30 }}
              className={cx(
                styles.Modal__Container,
                "elevation--1",
                // The open animation leaves the container translated 30px
                // up; on desktop the padding hides that, on a phone it would
                // cut off the top of the sheet.
                "max-md:!transform-none",
                {
                  [styles["Modal__Container--isFullWidth"]]: isFullWidth,
                  "max-md:!p-0": isWide,
                }
              )}
              {...(testId && { "data-testid": testId })}
            >
              <ModalContent innerRef={contentRef}>{children}</ModalContent>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalContext>
    </FocusLock>
  );
};
